import { useMemo } from 'react';
import { Box } from '@mui/material';
import { WeekPlan, WeekPlanTotals } from '../../core/types/weekPlan';
import { MassSummaryTable } from './MassSummaryTable';
import { CarsSummaryTable } from './CarsSummaryTable';
import { DataCard } from './DataCard';

interface SummaryProps {
  data: WeekPlan[];
}

const Summary = ({ data }: SummaryProps) => {
  const totals = useMemo(() => {
    if (!data) return null;
    return data.reduce<WeekPlanTotals>(
      (total, record) => ({
        mondayCars: total.mondayCars + record.mondayCars,
        tuesdayCars: total.tuesdayCars + record.tuesdayCars,
        wednesdayCars: total.wednesdayCars + record.wednesdayCars,
        thursdayCars: total.thursdayCars + record.thursdayCars,
        fridayCars: total.fridayCars + record.fridayCars,
        readyMade: total.readyMade + record.readyMade,
        environmentalReadyMade: total.environmentalReadyMade + record.environmentalReadyMade,
        blastedRockIn: total.blastedRockIn + record.blastedRockIn,
        blastedRockOut: total.blastedRockOut + record.blastedRockOut,
        mixedQuality: total.mixedQuality + record.mixedQuality,
        dumping: total.dumping + record.dumping,
        washing: total.washing + record.washing,
        contaminated: total.contaminated + record.contaminated,
      }),
      {
        mondayCars: 0,
        tuesdayCars: 0,
        wednesdayCars: 0,
        thursdayCars: 0,
        fridayCars: 0,
        readyMade: 0,
        environmentalReadyMade: 0,
        blastedRockIn: 0,
        blastedRockOut: 0,
        mixedQuality: 0,
        dumping: 0,
        washing: 0,
        contaminated: 0,
      },
    );
  }, [data]);

  const massIn = useMemo(() => {
    if (!totals) return 0;
    return totals.readyMade + totals.environmentalReadyMade + totals.blastedRockIn;
  }, [totals]);

  const massOut = useMemo(() => {
    if (!totals) return 0;
    return (
      totals.blastedRockOut
      + totals.mixedQuality
      + totals.dumping
      + totals.washing
      + totals.contaminated
    );
  }, [totals]);

  const totalMassAverage = useMemo(() => {
    if (!totals) return 0;
    const totalMass = massIn + massOut;
    return totalMass / 5;
  }, [totals, massIn, massOut]);

  const carsAverage = useMemo(() => {
    if (!totals) return 0;
    const totalCars = totals.mondayCars + totals.tuesdayCars + totals.wednesdayCars
      + totals.thursdayCars + totals.fridayCars;
    return totalCars / 5;
  }, [totals]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        justifyContent: 'space-around',
        gap: '2rem',
      }}
    >
      <Box sx={{ width: '32%' }}>
        {totals && <CarsSummaryTable totals={[totals]} />}
      </Box>
      <DataCard title="Bilsnitt" value={carsAverage} />
      {totals && <MassSummaryTable totals={[totals]} />}
      <DataCard title="Masse Inn" value={massIn} />
      <DataCard title="Masse Ut" value={massOut} />
      <DataCard title="Massnitt" value={totalMassAverage} />
    </Box>
  );
};

export default Summary;
